import {
  ConfigCommand,
  ConfigSlots,
  isBlankConfigValue,
  parseConfigCommand,
  parseConfigCommandList,
} from "./configCommands";
import { evaluateConfigExpression } from "./configExpression";
import { CsvJsonRow } from "./csvToJson";

export const accentColorCommandSlots = [
  "AccPaint",
  "AccPaint2",
  "AccCarbon",
  "AccPaintLow",
  "CarbonPer",
  "CarbonPerDev",
  "MirrorCapPaint",
];

// Creates stable React-friendly IDs from display text.
function createId(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

// Builds the full menu structure from CSV rows, including command/prerequisite metadata.
export function buildMenuDataFromCsvRows(rows: CsvJsonRow[]): MenuData {
  const categories = rows.reduce<MenuCategoryData[]>((menuCategories, row) => {
    // Skip rows that are explicitly disabled by the Include In Menu column.
    if (!shouldIncludeCsvRow(row)) {
      return menuCategories;
    }

    // Pull out the CSV fields that define where this option appears in the menu.
    const categoryTitle = row.Category;
    const subCategoryTitle = row["Category Subsection"];
    const optionValue = getOptionDisplayValue(row);

    if (!categoryTitle || !subCategoryTitle || !optionValue) {
      return menuCategories;
    }

    // Reuse an existing category, or create it the first time we see it.
    let category = menuCategories.find(
      (menuCategory) => menuCategory.title === categoryTitle,
    );

    if (!category) {
      category = {
        id: createId(categoryTitle),
        title: categoryTitle,
        subCategories: [],
      };
      menuCategories.push(category);
    }

    // Reuse an existing subsection inside the category, or create a new one.
    let subCategory = category.subCategories.find(
      (categorySubCategory) => categorySubCategory.title === subCategoryTitle,
    );

    if (!subCategory) {
      subCategory = {
        id: `${category.id}-${createId(subCategoryTitle)}`,
        title: subCategoryTitle,
        options: [],
      };
      category.subCategories.push(subCategory);
    }

    const optionCommand =
      row.Command || `${subCategory.id}-${subCategory.options.length}`;

    // Bare legacy swatch names live in ext; folder-prefixed values are relative
    // to the shared Thumbnails folder.
    const Thumbnail = normalizeThumbnailPath(row["Thumbnail"]);
    const backgroundImage = Thumbnail
      ? "url('/assets/Thumbnails/" + Thumbnail + "')"
      : undefined;

    // console.log(backgroundImage);

    // Store display data plus parsed CSV behavior for the clickable option.
    subCategory.options.push({
      id: `${subCategory.id}-${subCategory.options.length}-${createId(optionCommand)}`,
      command: optionCommand,
      commandEntry: parseConfigCommand(optionCommand),
      cascadeCommands: parseConfigCommandList(row.Cascades),
      value: optionValue,
      toolTip: optionValue,
      backgroundImage,
      prerequisite: normalizeCsvExpression(row.Prerequisite),
      isSelectedOverride: normalizeCsvExpression(row["Is Selected Override"]),
      cameraTrigger: normalizeCsvExpression(row["Camera Trigger"]),
      raw: row,
    });

    return menuCategories;
  }, []);

  return {
    title: "MODEL",
    subTitle: "Select your configurations",
    categories,
  };
}

// Filters the full menu down to options whose prerequisites are currently true.
export function getAvailableMenuData(
  menuData: MenuData,
  slots: ConfigSlots,
): MenuData {
  return {
    ...menuData,
    categories: menuData.categories
      .map((category) => ({
        ...category,
        subCategories: category.subCategories
          .map((subCategory) => ({
            ...subCategory,
            options: subCategory.options.filter((option) =>
              isMenuOptionAvailable(option, slots),
            ),
          }))
          .filter((subCategory) => subCategory.options.length > 0),
      }))
      .filter((category) => category.subCategories.length > 0),
  };
}

// Checks whether one option should be visible for the current config slots.
export function isMenuOptionAvailable(
  option: MenuOptionData,
  slots: ConfigSlots,
) {
  return evaluateConfigExpression(option.prerequisite, slots).value;
}

// Checks whether one option's own command matches the current slots.
export function isMenuOptionCommandSelected(
  option: MenuOptionData,
  slots: ConfigSlots,
) {
  return Boolean(
    option.commandEntry &&
    slots[option.commandEntry.slot] === option.commandEntry.value,
  );
}

// Checks whether one option should render as selected.
export function isMenuOptionSelected(
  option: MenuOptionData,
  slots: ConfigSlots,
) {
  if (!isBlankConfigValue(option.isSelectedOverride)) {
    return evaluateConfigExpression(option.isSelectedOverride, slots).value;
  }

  return Boolean(
    option.commandEntry &&
    slots[option.commandEntry.slot] === option.commandEntry.value,
  );
}

// Builds the text shown under a category name from the category's currently selected options.
export function getCategorySelectionSubTitles(
  category: MenuCategoryData,
  slots: ConfigSlots,
) {
  const selectedLabels = category.subCategories.flatMap((subCategory) =>
    subCategory.options
      .filter((option) => isMenuOptionSelected(option, slots))
      .map((option) => option.value),
  );

  return Array.from(new Set(selectedLabels));
}

// Builds the subtitle lookup consumed by the category header UI.
export function getMenuCategorySubTitles(
  menuData: MenuData,
  slots: ConfigSlots,
) {
  return menuData.categories.reduce<Record<string, string[]>>(
    (subTitlesByCategory, category) => {
      subTitlesByCategory[category.id] = getCategorySelectionSubTitles(
        category,
        slots,
      );

      return subTitlesByCategory;
    },
    {},
  );
}

// Clears accent color slots when their color swatches are no longer available.
export function clearUnavailableAccentColorSlots(
  menuData: MenuData,
  slots: ConfigSlots,
) {
  return accentColorCommandSlots.reduce<ConfigSlots>((nextSlots, slotName) => {
    const currentValue = nextSlots[slotName];

    if (!currentValue) {
      return nextSlots;
    }

    const hasAvailableSelectedOption = menuData.categories.some((category) =>
      category.subCategories.some((subCategory) =>
        subCategory.options.some(
          (option) =>
            option.commandEntry?.slot === slotName &&
            option.commandEntry.value === currentValue,
        ),
      ),
    );

    if (hasAvailableSelectedOption) {
      return nextSlots;
    }

    return {
      ...nextSlots,
      [slotName]: "",
    };
  }, slots);
}

// Reads the Include In Menu column and excludes only explicit false/no values.
function shouldIncludeCsvRow(row: CsvJsonRow) {
  if (isAccentsFinishResetRow(row)) {
    return true;
  }

  const includeValue = row["Include In Menu"];

  if (isBlankConfigValue(includeValue)) {
    return true;
  }

  const normalizedValue = includeValue.trim().toLowerCase();
  return !["false", "f", "no", "n"].includes(normalizedValue);
}

// The Unreal Accents menu still uses this reset row as the "None" finish option.
function isAccentsFinishResetRow(row: CsvJsonRow) {
  return (
    row.Category === "Accents" &&
    row["Category Subsection"] === "Finish" &&
    row.Command === "Variant AccentsWing:Off" &&
    row["Include In Menu"]?.trim().toLowerCase() === "false"
  );
}

// Gives the Accents reset row the same user-facing role as Unreal's "None" option.
function getOptionDisplayValue(row: CsvJsonRow) {
  if (isAccentsFinishResetRow(row)) {
    return "None";
  }

  return row["Text Override"] || row.Command;
}

// Normalizes optional CSV expression fields so "-" behaves like an empty value.
function normalizeCsvExpression(value?: string) {
  return isBlankConfigValue(value) ? undefined : value?.trim();
}

function normalizeThumbnailPath(value?: string) {
  const rawValue = value?.trim();

  if (!rawValue || isBlankConfigValue(rawValue)) {
    return undefined;
  }

  const thumbnailPath = rawValue.replace(/^(\.\.\/)+/, "");

  return thumbnailPath.includes("/") ? thumbnailPath : `ext/${thumbnailPath}`;
}

// TYPES //
// One clickable option from the CSV, including both UI data and parsed behavior.
export type MenuOptionData = {
  id: string;
  command: string;
  commandEntry?: ConfigCommand;
  cascadeCommands: ConfigCommand[];
  value: string;
  toolTip?: string;
  backgroundImage?: string;
  prerequisite?: string;
  isSelectedOverride?: string;
  cameraTrigger?: string;
  raw: CsvJsonRow;
};

// A group of options inside a category, such as "Foundation" or "Studio".
export type MenuSubCategoryData = {
  id: string;
  title: string;
  options: MenuOptionData[];
};

// A top-level menu category, such as "Exterior Theme" or "Accents".
export type MenuCategoryData = {
  id: string;
  title: string;
  subCategories: MenuSubCategoryData[];
};

// The complete menu model consumed by the React UI.
export type MenuData = {
  title: string;
  subTitle: string;
  categories: MenuCategoryData[];
};
